import React, { useEffect, useState, useCallback } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { useAuth } from '../../lib/AuthContext';
import { toast, ToastContainer } from 'react-toastify';
import BookSessionModal from '../BookSessionModal';
import { useNavigate } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';

export default function DashboardCalendar({ bookingVersion, onBookingAdded }) {
  const { accessToken: token, user } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    console.log(
      "%c🔥 DASHBOARD CALENDAR RENDERED",
      "background:#10B981;color:white;padding:6px;border-radius:6px;font-size:14px"
    );
  }, []);

  const fetchAllBookings = useCallback(async () => {
    if (!token || !user) return;

    try {
      const res = await fetch(
        `https://quirkleai.com/api/bookings/?user_type=${user.role}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!res.ok) throw new Error('Failed to load sessions');

      const data = await res.json();
      const formatted = data.map((s) => ({
        id: s.id,
        title:
          user.username === s.therapist?.username
            ? `With ${s.client?.username}`
            : `With Dr. ${s.therapist?.username}`,
        start: s.start_time,
        end: s.end_time,
        extendedProps: {
          therapist: s.therapist,
          client: s.client,
          meeting_id: s.meeting_id,
        },
      }));

      setEvents(formatted);
    } catch (err) {
      toast.error('Error loading sessions');
    }
  }, [token, user]);

  useEffect(() => {
    fetchAllBookings();
  }, [fetchAllBookings, bookingVersion]);

  const handleDateClick = (info) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const clicked = new Date(info.dateStr + 'T00:00:00');

    if (clicked < today) return toast.warn('Cannot book past days.');

    setSelectedDate(info.dateStr);
    setShowModal(true);
  };

  const handleEventClick = (info) => {
    const now = new Date();
    const start = new Date(info.event.start);
    const end = new Date(info.event.end);

    const { meeting_id, therapist, client } = info.event.extendedProps;

    if (!meeting_id) return toast.error("Meeting ID missing.");

    const authorized =
      user.username === therapist?.username ||
      user.username === client?.username;

    if (!authorized) return toast.error("You are not part of this session.");

    if (now < start) return toast.info("Session has not started yet.");
    if (now > end) return toast.warn("Session already ended.");

    navigate(`/therapist/jitsi-room/${meeting_id}`);
  };

  const handleBookingSubmit = ({ start, end, client_id, therapist_id }) => {
    const payload = {
      start_time: start,
      end_time: end,
      ...(user.role === 'therapist' ? { client: client_id } : {}),
      ...(user.role === 'client' ? { therapist: therapist_id } : {}),
    };

    fetch('https://quirkleai.com/api/sessions/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    })
      .then((res) => {
        if (!res.ok) throw new Error();
        setShowModal(false);
        return res.json();
      })
      .then(() => onBookingAdded && onBookingAdded())
      .catch(() => toast.error("Booking failed."));
  };

  return (
    <div className="p-0 fc-therapist">

      <div className="w-full overflow-x-auto sm:overflow-visible">
        <div className="min-w-[360px] md:min-w-full">

          <FullCalendar
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            headerToolbar={{
              left: 'prev,next',
              center: 'title',
              right: 'today',
            }}
            events={events}
            dateClick={handleDateClick}
            eventClick={handleEventClick}
            height="auto"
            dayMaxEvents={2}
            eventClassNames={() => [
              '!bg-indigo-700',
              '!text-white',
              'rounded-md',
              'px-2',
              'py-0.5',
              'text-xs',
              'font-medium',
            ]}
          />

        </div>
      </div>

      {showModal && (
        <BookSessionModal
          token={token}
          date={selectedDate}
          onClose={() => setShowModal(false)}
          onSubmit={handleBookingSubmit}
        />
      )}

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
}
